import studioLookAndFeelSections from '@/studio/lookAndFeel/sections';
import logger from '@goldcast/logger';
import { showErrorNotification } from '@/utils/utils.js';
import { themeHelper } from '@goldcast/uicomponents-branding';
import { partialUpdateStudioEventTemplate, partialUpdateEvent, updateThemeObjEvent } from '@goldcast/api/backend';

export default {
  changeView(context, view) {
    // This is required because many things in the components render only
    // once when they are opened. If we just change the size of the rendered
    // component not all responsive things work. But if we render it from
    // scratch they do. For example the title text of the event.
    context.commit('setSwitchingView', true);
    const currentTab = context.state.currentTab;
    context.commit('changeCurrentTab', '');

    return context
      .dispatch('loadUicomponentStyles')
      .then(() => {
        const oldStyleNodes = document.querySelectorAll('style#uicomponentstyles');
        oldStyleNodes.forEach(oldStyleNode => {
          document.head.removeChild(oldStyleNode);
        });
        const newStyleNode = document.createElement('style');
        newStyleNode.setAttribute('id', 'uicomponentstyles');
        if (view === 'cellphone') {
          newStyleNode.innerText = context.state.cellphoneStyles;
        } else {
          newStyleNode.innerText = context.state.monitorStyles;
        }
        document.head.appendChild(newStyleNode);
      })
      .then(() => {
        context.commit('setCurrentView', view);
        context.commit('setSwitchingView', false);
        setTimeout(() => {
          context.commit('changeCurrentTab', currentTab);
        }, 700);
        window.mobileCheck = function () {
          return view === 'cellphone';
        };
      })
      .catch(err => {
        logger.error('Failed when trying to download styles', err);
      });
  },
  loadUicomponentStyles(context) {
    if (context.state.cellphoneStyles) {
      return 'already fetched';
    } else {
      let prefix = process.env.NODE_ENV === 'production' ? '' : '';
      const p1 = fetch(`${prefix}/uicomponents.css`)
        .then(res => res.text())
        .then(res => {
          context.commit('setMonitorStyles', res);
        });
      const p2 = fetch(`${prefix}/mobuicomponents.css`)
        .then(res => res.text())
        .then(res => {
          context.commit('setCellphoneStyles', res);
        });

      return Promise.all([p1, p2]);
    }
  },
  setInitialEventBrandingState(context, event) {
    let brandSettings = {};
    if (!event) return;

    const {
      theme_type,
      stage_background,
      mobile_stage_background,
      virtual_background_url,
      primary_background_shade,
      alt_primary_background_shade,
      sidebar_alpha,
      sidebar_blur,
      event_background,
      mobile_event_background,
      primary_text_color,
      theme_font_name,
      theme_font_link,
      font_type,
      custom_font_name,
      screen_saver_background,
      screen_saver_text_color,
      splash_gradient_height,
      splash_background_color,
      splash_background_shade,
      splash_shade_alpha,
      splash_theme_font_src,
      splash_theme_font_link,
      splash_font_type,
      splash_theme_font_name,
      splash_text_color,
      is_overlay_visible,
      custom_demo_cta_link_to,
      custom_demo_cta_button_url,
      custom_cta_enabled,
      custom_cta_link_to,
      custom_cta_button_text,
      custom_cta_button_url
    } = event.theme_obj;

    const {
      logo_url,
      favicon,
      broadcast_filler_image_s3_path,
      demo_link_enabled,
      on_demand_demo_link_enabled,
      demo_request_button_text,
      demo_request_title,
      demo_request_message,
      demo_request_submit_type,
      demo_request_consent_title,
      demo_request_disclosure_text,
      is_title_visible,
      is_date_visible,
      is_logo_visible,
      title,
      stage_roll_video_asset,
      filler_slide,
      broadcast_filler_video_s3_path,
      social_media_thumbnail,
      broadcast_chat_disabled,
      public_chat_disabled,
      chat_visibilty,
      p2p_chat_disabled,
      thread_chat_disabled,
      is_text_qna_moderated,
      anonymous_qna_enabled,
      show_answerers_identity,
      textqna_answerers_alias,
      gifs_enabled,
      discussions_chat_disabled,
      booth_chat_disabled,
      have_text_qna,
      have_video_qna,
      show_discussion_rooms,
      discussion_room_tab_title,
      show_event_booths,
      booth_tab_title,
      booth_tab_title_2,
      show_attendees,
      people_tab_title,
      show_people_tab_speakers,
      speaker_tab_visible,
      speaker_tab_title,
      disable_enter_event_button,
      enable_tech_check,
      show_onboarding,
      tab_descriptions,
      assign_speaker_qna,
      show_attendee_profiles,
      floating_emoji_reactions_enabled
    } = event;

    brandSettings = {
      ...event.theme_obj,
      theme_type: theme_type || 'colorful',
      logo_url,
      favicon,
      stage_background,
      mobile_stage_background,
      broadcast_filler_image_s3_path,
      demo_link_enabled,
      on_demand_demo_link_enabled,
      demo_request_button_text,
      demo_request_title,
      demo_request_message,
      demo_request_submit_type,
      demo_request_consent_title,
      demo_request_disclosure_text,
      virtual_background_url,
      primary_background_shade,
      alt_primary_background_shade,
      sidebar_alpha,
      sidebar_blur,
      event_background,
      mobile_event_background,
      is_title_visible,
      is_date_visible,
      is_logo_visible,
      primary_text_color,
      theme_font_name,
      theme_font_link,
      font_type,
      custom_font_name,
      title,
      stage_roll_video_asset,
      screen_saver_background,
      screen_saver_text_color,
      splash_gradient_height,
      filler_slide,
      broadcast_filler_video_s3_path,
      social_media_thumbnail,
      splash_background_color,
      splash_background_shade,
      splash_shade_alpha,
      splash_theme_font_src,
      splash_theme_font_link,
      splash_font_type,
      splash_theme_font_name,
      splash_text_color,
      broadcast_chat_disabled,
      public_chat_disabled,
      chat_visibilty,
      p2p_chat_disabled,
      thread_chat_disabled,
      is_text_qna_moderated,
      anonymous_qna_enabled,
      show_answerers_identity,
      textqna_answerers_alias,
      gifs_enabled,
      discussions_chat_disabled,
      booth_chat_disabled,
      have_text_qna,
      have_video_qna,
      show_discussion_rooms,
      discussion_room_tab_title,
      show_event_booths,
      booth_tab_title,
      booth_tab_title_2,
      show_attendees,
      people_tab_title,
      show_people_tab_speakers,
      speaker_tab_visible,
      speaker_tab_title,
      disable_enter_event_button,
      enable_tech_check,
      show_onboarding,
      tab_descriptions,
      is_overlay_visible,
      custom_demo_cta_link_to,
      custom_demo_cta_button_url,
      custom_cta_enabled,
      custom_cta_link_to,
      custom_cta_button_text,
      custom_cta_button_url,
      assign_speaker_qna,
      show_attendee_profiles,
      floating_emoji_reactions_enabled
    };

    context.commit('setBrandSettings', brandSettings);
    context.commit('setBrandingFor', 'event');
    context.commit('setCurrentView', 'monitor');

    // Only load regular font if it's not Custom type or if Custom type has a valid source
    if (
      brandSettings.font_type !== 'Custom' ||
      (brandSettings.font_type === 'Custom' && brandSettings.theme_font_src)
    ) {
      themeHelper.loadFont(brandSettings.font_type, brandSettings.theme_font_src, brandSettings.theme_font_link);
    }

    // Only load splash font if it's not Custom Splash type or if Custom Splash type has a valid source
    if (
      brandSettings.splash_font_type !== 'Custom Splash' ||
      (brandSettings.splash_font_type === 'Custom Splash' && brandSettings.splash_theme_font_src)
    ) {
      themeHelper.loadFont(
        brandSettings.splash_font_type,
        brandSettings.splash_theme_font_src,
        brandSettings.splash_theme_font_link
      );
    }
  },
  setInitialTemplateBrandingState(context, template) {
    let brandSettings = {};
    if (!template) return;

    const {
      theme_type,
      stage_background,
      mobile_stage_background,
      virtual_background_url,
      primary_background_shade,
      alt_primary_background_shade,
      sidebar_alpha,
      sidebar_blur,
      event_background,
      mobile_event_background,
      primary_text_color,
      theme_font_name,
      theme_font_link,
      font_type,
      custom_font_name,
      screen_saver_background,
      screen_saver_text_color,
      splash_gradient_height,
      splash_background_color,
      splash_background_shade,
      splash_shade_alpha,
      splash_theme_font_src,
      splash_theme_font_link,
      splash_font_type,
      splash_theme_font_name,
      splash_text_color,
      is_overlay_visible,
      custom_cta_enabled,
      custom_demo_cta_link_to,
      custom_demo_cta_button_url,
      custom_cta_link_to,
      custom_cta_button_text,
      custom_cta_button_url
    } = template.theme_obj;

    const {
      logo_url,
      favicon,
      broadcast_filler_image_s3_path,
      demo_link_enabled,
      demo_request_button_text,
      demo_request_title,
      demo_request_message,
      demo_request_submit_type,
      demo_request_consent_title,
      demo_request_disclosure_text,
      is_title_visible,
      is_date_visible,
      is_logo_visible,
      stage_roll_video_asset,
      filler_slide,
      broadcast_filler_video_s3_path,
      social_media_thumbnail,
      broadcast_chat_disabled,
      public_chat_disabled,
      chat_visibilty,
      p2p_chat_disabled,
      thread_chat_disabled,
      is_text_qna_moderated,
      anonymous_qna_enabled,
      show_answerers_identity,
      textqna_answerers_alias,
      gifs_enabled,
      discussions_chat_disabled,
      booth_chat_disabled,
      have_text_qna,
      have_video_qna,
      show_discussion_rooms,
      discussion_room_tab_title,
      show_event_booths,
      booth_tab_title,
      booth_tab_title_2,
      show_attendees,
      people_tab_title,
      show_people_tab_speakers,
      speaker_tab_visible,
      speaker_tab_title,
      disable_enter_event_button,
      enable_tech_check,
      show_onboarding,
      assign_speaker_qna,
      show_attendee_profiles,
      floating_emoji_reactions_enabled
    } = template;

    brandSettings = {
      ...template.theme_obj,
      theme_type: theme_type || 'colorful',
      logo_url,
      favicon,
      stage_background,
      mobile_stage_background,
      broadcast_filler_image_s3_path,
      demo_link_enabled,
      demo_request_button_text,
      demo_request_title,
      demo_request_message,
      demo_request_submit_type,
      demo_request_consent_title,
      demo_request_disclosure_text,
      virtual_background_url,
      primary_background_shade,
      alt_primary_background_shade,
      sidebar_alpha,
      sidebar_blur,
      event_background,
      mobile_event_background,
      is_title_visible,
      is_date_visible,
      is_logo_visible,
      primary_text_color,
      theme_font_name,
      theme_font_link,
      font_type,
      custom_font_name,
      title: 'Your amazing event',
      stage_roll_video_asset,
      screen_saver_background,
      screen_saver_text_color,
      splash_gradient_height,
      filler_slide,
      broadcast_filler_video_s3_path,
      social_media_thumbnail,
      splash_background_color,
      splash_background_shade,
      splash_shade_alpha,
      splash_theme_font_src,
      splash_theme_font_link,
      splash_font_type,
      splash_theme_font_name,
      splash_text_color,
      broadcast_chat_disabled,
      public_chat_disabled,
      chat_visibilty,
      p2p_chat_disabled,
      thread_chat_disabled,
      is_text_qna_moderated,
      anonymous_qna_enabled,
      show_answerers_identity,
      textqna_answerers_alias,
      gifs_enabled,
      discussions_chat_disabled,
      booth_chat_disabled,
      have_text_qna,
      have_video_qna,
      show_discussion_rooms,
      discussion_room_tab_title,
      show_event_booths,
      booth_tab_title,
      booth_tab_title_2,
      show_attendees,
      people_tab_title,
      show_people_tab_speakers,
      speaker_tab_visible,
      speaker_tab_title,
      disable_enter_event_button,
      enable_tech_check,
      show_onboarding,
      is_overlay_visible,
      custom_demo_cta_link_to,
      custom_demo_cta_button_url,
      custom_cta_enabled,
      custom_cta_link_to,
      custom_cta_button_text,
      custom_cta_button_url,
      assign_speaker_qna,
      show_attendee_profiles,
      floating_emoji_reactions_enabled
    };

    context.commit('setBrandSettings', brandSettings);
    context.commit('setBrandingFor', 'template');
    context.commit('setCurrentView', 'monitor');

    if (
      brandSettings.font_type !== 'Custom' ||
      (brandSettings.font_type === 'Custom' && brandSettings.theme_font_src)
    ) {
      themeHelper.loadFont(brandSettings.font_type, brandSettings.theme_font_src, brandSettings.theme_font_link);
    }

    if (
      brandSettings.splash_font_type !== 'Custom Splash' ||
      (brandSettings.splash_font_type === 'Custom Splash' && brandSettings.splash_theme_font_src)
    ) {
      themeHelper.loadFont(
        brandSettings.splash_font_type,
        brandSettings.splash_theme_font_src,
        brandSettings.splash_theme_font_link
      );
    }
  },
  async updateBranding({ state, dispatch, rootGetters, commit, rootState }) {
    if (state.isCurrentModuleInValid) {
      showErrorNotification('Please fill the required fields');
      return;
    }

    const currentEvent = rootGetters['studioEventList/getCurrentEvent'];
    const eventId = currentEvent?.id;

    const currentTemplate = rootGetters['studioTemplateList/getCurrentTemplate'];
    const templateId = currentTemplate?.id;

    const changesMadeInThemeObj = state.changesMadeInThemeObj;
    const changesMadeInEvent = state.changesMadeInEvent;

    const isUpdatingTemplate = state.for === 'template';

    commit('setUpdateBrandingLoading', true);

    if (!isEmptyObj(changesMadeInEvent) && isUpdatingTemplate) {
      try {
        const encodedEventChanges = encodeEventImageUrls(changesMadeInEvent);
        const newTemplateData = await partialUpdateStudioEventTemplate({ id: templateId, body: encodedEventChanges });
        commit('setBrandSettings', changesMadeInEvent);
        commit('studioTemplateList/updateTemplate', newTemplateData, {
          root: true
        });
      } catch (error) {
        showErrorNotification("Branding couldn't be updated, please try again");
        commit('setUpdateBrandingError', true);
      }
    } else if (!isEmptyObj(changesMadeInEvent)) {
      try {
        const encodedEventChanges = encodeEventImageUrls(changesMadeInEvent);
        const newEventData = await partialUpdateEvent({
          id: eventId,
          body: { ...encodedEventChanges, user_intent: 'look_and_feel_update' }
        });
        commit('setBrandSettings', changesMadeInEvent);
        commit('studioEventList/partialUpdateEvent', { eventId, partialEvent: newEventData }, { root: true });
      } catch (error) {
        let errorNotificationMessage = `Branding couldn't be updated, please try again`;
        if (error && error['apiErrorCode']) {
          if (error['apiErrorCode'] === 'VIDEO_DOESNOT_BELONG_TO_ORGANIZATION') {
            errorNotificationMessage = `Video Asset does not belong to Event's Organization`;
          } else if (error['apiErrorCode'] === 'UNPROCESSED_VIDEO_FOR_STAGE_ROLL') {
            errorNotificationMessage = `Cannot add an unprocessed video as the event stage roll video.`;
          }
        }
        showErrorNotification(errorNotificationMessage);
        commit('setUpdateBrandingError', true);
      }
    }

    // TODO add check for error condition
    if (!isEmptyObj(changesMadeInThemeObj) && isUpdatingTemplate) {
      try {
        const newTemplateData = await partialUpdateStudioEventTemplate({
          id: templateId,
          body: { theme_obj: changesMadeInThemeObj }
        });
        commit('setBrandSettings', changesMadeInThemeObj);
        commit('studioTemplateList/updateTemplate', newTemplateData, {
          root: true
        });
      } catch (error) {
        showErrorNotification("Branding couldn't be updated, please try again");
        commit('setUpdateBrandingError', true);
      }
    } else if (!isEmptyObj(changesMadeInThemeObj)) {
      try {
        const newEventData = await updateThemeObjEvent({ id: eventId, body: { theme_obj: changesMadeInThemeObj } });
        commit('setBrandSettings', changesMadeInThemeObj);
        commit('studioEventList/partialUpdateEvent', { eventId, partialEvent: newEventData }, { root: true });
      } catch {
        commit('setUpdateBrandingError', true);
      }
    }

    commit('setUpdateBrandingLoading', false);
  },
  onClickNext({ commit, state, rootGetters }) {
    const { currentSectionId, currentModuleId } = state;
    // find next module index
    let nextModuleIndex;
    const currentSectionObj = studioLookAndFeelSections.find(section => section.id === currentSectionId);
    const currentSectionModulesArr = currentSectionObj.modules;
    currentSectionModulesArr.find((module, index) => {
      if (module.id === currentModuleId) {
        nextModuleIndex = index + 1;
      }
    });

    if (nextModuleIndex < currentSectionModulesArr.length) {
      // go to next module in current section
      commit('setSection', {
        sectionId: currentSectionId,
        moduleId: currentSectionModulesArr[nextModuleIndex].id
      });
    } else {
      // if next module is not present then move to next section
      // find next section index
      let nextSectionIndex;
      studioLookAndFeelSections.find((section, index) => {
        if (section.id === currentSectionId) {
          nextSectionIndex = index + 1;
        }
      });
      // if next section is not present then return from here otherwise go to next section
      if (!studioLookAndFeelSections[nextSectionIndex]) return;
      commit('setSection', {
        sectionId: studioLookAndFeelSections[nextSectionIndex].id,
        moduleId: studioLookAndFeelSections[nextSectionIndex].modules[0].id
      });
    }
    // mark the current module completed
    commit('setComplete', currentModuleId);
  },
  onSelectModule({ state, commit }, moduleId) {
    if (state.isCurrentModuleInValid) return;
    const { currentSectionId } = state;
    commit('setSection', {
      sectionId: currentSectionId,
      moduleId: moduleId
    });
  }
};

function isEmptyObj(obj) {
  return Object.entries(obj).length === 0 && obj.constructor === Object;
}

function encodeEventImageUrls(changes) {
  const urlFields = ['social_media_thumbnail', 'logo_url', 'favicon'];
  const result = { ...changes };
  urlFields.forEach(field => {
    if (result[field] != null) {
      // decodeURI first to avoid double-encoding already-encoded URLs (e.g. %20 → %2520).
      // However, decodeURI throws a URIError if the URL contains a bare % that isn't part
      // of a valid percent-encoded sequence (e.g. "50%_off.png"). In that case, fall back
      // to encoding the raw string directly so the % itself gets safely escaped to %25.
      try {
        result[field] = encodeURI(decodeURI(result[field]));
      } catch {
        result[field] = encodeURI(result[field]);
      }
    }
  });
  return result;
}
